import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  ArrowLeft, 
  HelpCircle, 
  ChevronDown, 
  ChevronRight,
  Calculator,
  DoorOpen,
  Car,
  Fingerprint,
  Shield,
  Save,
  FileText,
  Users,
  Lightbulb,
  Package,
  Clock,
  CheckCircle
} from "lucide-react";

const HelpGuide = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    speedlanes: false,
    atlas: false,
    biocv: false,
    parking: false,
    turnstiles: false,
    halfheight: false,
    visitor: false,
    advancedparking: false,
    amazon: false,
    tips: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const Section = ({ id, title, icon: Icon, children, color = "blue" }) => {
    const isExpanded = expandedSections[id];
    const colorClasses = {
      blue: "border-blue-200 bg-blue-50",
      green: "border-green-200 bg-green-50",
      orange: "border-orange-200 bg-orange-50",
      purple: "border-purple-200 bg-purple-50",
      amber: "border-amber-200 bg-amber-50"
    };

    return (
      <Collapsible open={isExpanded} onOpenChange={() => toggleSection(id)}>
        <Card className={`mb-4 ${isExpanded ? colorClasses[color] : ''}`}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`h-6 w-6 text-${color}-600`} />
                  <CardTitle className="text-lg">{title}</CardTitle>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-slate-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-500" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {children}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    );
  };

  const Step = ({ number, title, children }) => (
    <div className="flex gap-4 mb-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
        <div className="text-slate-600 text-sm">{children}</div>
      </div>
    </div>
  );

  const Tip = ({ children }) => (
    <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 my-3">
      <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-800">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <HelpCircle className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">How to Use the Configurator</h1>
              <p className="text-slate-600">Step-by-step guide for creating quotes</p>
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <Section id="overview" title="Getting Started" icon={CheckCircle} color="green">
          <div className="space-y-4">
            <p className="text-slate-700">
              The ZKTeco UK Solution Configurator helps you quickly create accurate quotes for security solutions. 
              Each configurator guides you through product selection with automatic pricing based on your role.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">For ZKTeco Staff</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Select quote target (Distributor/Installer)</li>
                  <li>• See all pricing tiers</li>
                  <li>• Generate quotes for any customer</li>
                  <li>• Manage all user accounts</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">For Distributors</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• See both cost and sell prices</li>
                  <li>• Apply custom discounts</li>
                  <li>• View margin calculations</li>
                  <li>• Manage installer accounts</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">For Installers</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• See installer sell prices</li>
                  <li>• Create and save quotes</li>
                  <li>• Download quote images</li>
                  <li>• Access product datasheets</li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-amber-800 mb-2">Quote Target Selection (ZKTeco Staff Only)</h4>
              <p className="text-sm text-amber-700">
                When creating quotes as ZKTeco staff, you&apos;ll be asked &quot;Who is this quote for?&quot; with options for Distributor, Installer, or End User.
                This determines which pricing columns appear in the Bill of Materials:
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• <strong>Distributor</strong> - Shows both Distributor Price (purple) and Installer Price (green)</li>
                <li>• <strong>Installer</strong> - Shows only Installer Price (green)</li>
              </ul>
            </div>

            <Tip>
              All quotes auto-save to your account. Access them anytime from the &quot;Saved Quotes&quot; section on the dashboard.
            </Tip>
          </div>
        </Section>

        {/* Speedlanes Section */}
        <Section id="speedlanes" title="Comet Speedlanes Configurator" icon={DoorOpen} color="green">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure pedestrian speedlanes with various options for glass panels, access credentials, and balustrade infills.
            </p>

            <Step number="1" title="Enter Project Details">
              Enter a project name to identify your quote. Optionally add a quote reference for your records.
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                <strong>ZKTeco Staff:</strong> You&apos;ll be asked &quot;Who is this quote for?&quot; - select Distributor, Installer, or End User to show the appropriate pricing columns in the BOM.
              </div>
            </Step>

            <Step number="2" title="Configure Bank(s)">
              Set up one or more speedlane banks:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Total Lanes</strong> - Number of passages needed</li>
                <li><strong>DDA Lanes</strong> - Wide lanes for wheelchair access</li>
                <li><strong>High Wings</strong> - Optional 1.8m wings for enhanced security</li>
              </ul>
            </Step>

            <Step number="3" title="Base Requirements">
              Choose whether you need Comet bases. Bases allow for cable routing without floor modifications.
            </Step>

            <Step number="4" title="R50 Balustrade (NEW)">
              If installing in a fixed-width space, add balustrade to fill gaps:
              <ul className="list-disc ml-4 mt-1">
                <li>Select &quot;Yes&quot; for &quot;Do you require balustrade?&quot;</li>
                <li>Enter the <strong>total space width</strong> in mm</li>
                <li>The system calculates the gap and required R50 frames + infill panels</li>
                <li>Each infill (max 1m) gets its own R50 frame (2 uprights @ 25mm each)</li>
                <li>For multiple infills, all panels are cut to equal width</li>
              </ul>
              <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-sm">
                <strong>Example:</strong> 3500mm space with 2-lane bank (1969mm) = 1531mm gap = 2x R50 frames + 2x infill panels @ 716mm each
              </div>
            </Step>

            <Step number="5" title="Access Control Options">
              Choose your credential verification method:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Face Recognition</strong> - Contactless biometric</li>
                <li><strong>Palm Recognition</strong> - Palm vein biometric</li>
                <li><strong>RFID Card</strong> - Proximity cards</li>
                <li><strong>QR Code</strong> - Mobile/visitor credentials</li>
                <li><strong>Bluetooth</strong> - Mobile credential via Bluetooth</li>
              </ul>
            </Step>

            <Step number="6" title="Extended Warranty">
              Optionally add extended warranty coverage (4 or 5 years).
            </Step>

            <Step number="7" title="Generate Quote">
              Click &quot;Save &amp; Calculate&quot; to see the complete Bill of Materials with pricing. The BOM includes:
              <ul className="list-disc ml-4 mt-1">
                <li>Speedlane units (masters, slaves, DDA)</li>
                <li>Bases (if selected)</li>
                <li>R50 balustrade with custom infill widths (if selected)</li>
                <li>Access control readers</li>
                <li>Warranty extensions</li>
              </ul>
            </Step>

            <Tip>
              Click on any SKU link in the results to view the product datasheet. You can also add sundry items manually before downloading.
            </Tip>
          </div>
        </Section>

        {/* Atlas Access Control Section */}
        <Section id="atlas" title="Atlas Access Control Configurator" icon={Fingerprint} color="blue">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure door access control systems with Atlas controllers and various credential types.
            </p>

            <Step number="1" title="Select Credential Type">
              Choose how users will authenticate:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>RFID</strong> - Proximity cards (most common)</li>
                <li><strong>Fingerprint</strong> - Fingerprint biometric readers</li>
                <li><strong>Pin and RFID</strong> - PIN code with card combination</li>
                <li><strong>QR Code</strong> - Mobile/visitor credentials</li>
              </ul>
            </Step>

            <Step number="2" title="Read In/Out Configuration">
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Read In Only</strong> - Reader on entry side only (free exit)</li>
                <li><strong>Read In/Out</strong> - Readers on both sides of door</li>
              </ul>
            </Step>

            <Step number="3" title="Enter Number of Doors">
              Specify the total number of doors to control. The system calculates the optimal controller configuration.
            </Step>

            <Step number="4" title="Review Controller Breakdown">
              The configurator shows which controllers are needed and how readers are distributed:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>1-door controller</strong> - For single door installations</li>
                <li><strong>2-door controller</strong> - For small to medium setups</li>
                <li><strong>4-door controller</strong> - For larger installations, most cost-effective per door</li>
              </ul>
            </Step>

            <Tip>
              The configurator automatically selects the optimal combination of controllers to minimise cost while meeting your requirements.
            </Tip>
          </div>
        </Section>

        {/* Bio CVSecurity Section */}
        <Section id="biocv" title="Bio CVSecurity Configurator" icon={Shield} color="purple">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure cloud-based access control with advanced biometrics and modular add-ons.
            </p>

            <Step number="1" title="Select Modules">
              Choose which modules you need:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Access Control</strong> - Core door control (required)</li>
                <li><strong>Time &amp; Attendance</strong> - Employee time tracking</li>
                <li><strong>Elevator Control</strong> - Floor access management</li>
                <li><strong>Parking Management</strong> - Vehicle access</li>
              </ul>
            </Step>

            <Step number="2" title="Configure Access Control">
              For the Access Control module:
              <ul className="list-disc ml-4 mt-1">
                <li>Enter number of admin licenses needed</li>
                <li>Specify number of doors</li>
                <li>Select credential types (Face, Palm, RFID, etc.)</li>
                <li>Choose if card printers are needed</li>
              </ul>
            </Step>

            <Step number="3" title="Bluetooth Credentials (Optional)">
              If using Bluetooth mobile credentials:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Annual License</strong> - Reassignable, subscription-based</li>
                <li><strong>Permanent License</strong> - One-time purchase, device-tied</li>
              </ul>
            </Step>

            <Step number="4" title="Generate Quote">
              Review the complete system configuration and pricing breakdown by module.
            </Step>

            <Tip>
              Bio CVSecurity uses cloud hosting. Server costs are based on employee count tiers shown in the quote.
            </Tip>
          </div>
        </Section>

        {/* Parking Barriers Section */}
        <Section id="parking" title="Parking Barriers Configurator" icon={Car} color="orange">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure parking barrier systems with various boom lengths and accessories.
            </p>

            <Step number="1" title="Select Number of Lanes">
              Enter how many barrier lanes you need to control.
            </Step>

            <Step number="2" title="Choose Barrier Type">
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Standard</strong> - Basic barrier unit</li>
                <li><strong>LED Boom</strong> - Illuminated boom arm for visibility</li>
              </ul>
            </Step>

            <Step number="3" title="Select Boom Length">
              Choose the appropriate boom length for your lane width (3m, 4m, 5m, or 6m options).
            </Step>

            <Step number="4" title="Configure Handed">
              Select left or right-handed based on mounting position relative to traffic flow.
            </Step>

            <Step number="5" title="Add Accessories">
              Optional accessories include:
              <ul className="list-disc ml-4 mt-1">
                <li>LPR Cameras (License Plate Recognition)</li>
                <li>Safety Radar</li>
                <li>Loop Detectors</li>
                <li>Warning Lights</li>
                <li>Remote Fobs</li>
              </ul>
            </Step>

            <Tip>
              Safety radar is recommended for high-traffic areas to prevent boom contact with vehicles.
            </Tip>
          </div>
        </Section>

        {/* Full Height Turnstiles Section */}
        <Section id="turnstiles" title="Full Height Turnstiles Configurator" icon={DoorOpen} color="amber">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure full height turnstiles for maximum security perimeter control.
            </p>

            <Step number="1" title="Enter Quantity">
              Specify how many turnstile units are needed.
            </Step>

            <Step number="2" title="Select Finish Type">
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Powder Coated</strong> - Durable colored finish</li>
                <li><strong>Stainless Steel</strong> - Premium appearance</li>
              </ul>
            </Step>

            <Step number="3" title="Choose Turnstile Type">
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Single</strong> - One passage, one direction</li>
                <li><strong>Double</strong> - One passage, bi-directional</li>
              </ul>
            </Step>

            <Step number="4" title="Select Arm Configuration">
              Choose 3-arm (120°) or 4-arm (90°) rotation based on throughput needs.
            </Step>

            <Step number="5" title="Cover Type">
              Select canopy/cover options for weather protection if installed outdoors.
            </Step>

            <Tip>
              3-arm turnstiles allow faster throughput, while 4-arm provides better anti-passback control.
            </Tip>
          </div>
        </Section>

        {/* Half Height Turnstiles Section */}
        <Section id="halfheight" title="Half Height Turnstiles Configurator" icon={DoorOpen} color="blue">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Configure half height (waist-high) turnstiles for indoor access control with a sleeker aesthetic.
            </p>

            <Step number="1" title="Enter Project Details">
              Enter a project name. For distributors/ZKTeco staff, you can apply an installer discount percentage.
            </Step>

            <Step number="2" title="Select Products">
              Choose from the available turnstile models:
              <ul className="list-disc ml-4 mt-1">
                <li><strong>TS1000</strong> - Standard tripod turnstile</li>
                <li><strong>TS2000</strong> - Premium tripod turnstile</li>
                <li><strong>SBT3000</strong> - Swing barrier (includes required wings)</li>
              </ul>
            </Step>

            <Step number="3" title="Base Requirements">
              Choose whether you need bases for cable routing without floor modifications.
            </Step>

            <Step number="4" title="Quote Target (ZKTeco Staff)">
              ZKTeco staff can select Distributor, Installer, or End User to show appropriate pricing columns in the BOM.
            </Step>

            <Step number="5" title="Generate Quote">
              Click &quot;Create Quote &amp; Save&quot; to generate the BOM. The system automatically adds required accessories like wings for SBT3000 units.
            </Step>

            <Tip>
              SBT3000 swing barriers require matching wings which are automatically added to your quote.
            </Tip>
          </div>
        </Section>

        {/* Visitor Management Section */}
        <Section id="visitor" title="Visitor Management Configurator" icon={Users} color="blue">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Software Visitor Management lets you quote visitor check-in, host notifications and pass printing.
              It is now delivered as a module <strong>inside the Bio CVSecurity configurator</strong>, so you can
              combine Access Control, Time &amp; Attendance and Visitor Management in a single quote.
            </p>

            <Step number="1" title="Open Bio CVSecurity">
              From the dashboard, go to <strong>Access Control and Visitor Management → Bio CVSecurity</strong>.
            </Step>

            <Step number="2" title="Enable the Visitor Management Module">
              In the module selection step, tick <strong>Visitor Management</strong> alongside any other modules you need
              (Access Control, Time &amp; Attendance, etc.).
            </Step>

            <Step number="3" title="Configure & Generate Quote">
              Enter the required licence quantities and options, then generate the quote. Visitor Management line items
              appear within the combined Bio CVSecurity Bill of Materials.
            </Step>

            <Tip>
              Combining modules in one Bio CVSecurity quote keeps server/hosting costs consolidated rather than quoting them separately.
            </Tip>
          </div>
        </Section>

        {/* Advanced Parking Solutions Section */}
        <Section id="advancedparking" title="Advanced Parking Solutions" icon={Car} color="orange">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              Advanced car park and paid-for parking management (ticketing, payment and ANPR-based car park control).
            </p>

            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <Clock className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <div className="text-sm text-emerald-800">
                <strong>Coming Soon.</strong> This module is in development. You&apos;ll find it under
                <strong> Parking Barrier Systems → Advanced Parking Solutions</strong>. For paid-parking projects in the
                meantime, use <strong>Request Project Support</strong> on the dashboard.
              </div>
            </div>
          </div>
        </Section>

        {/* Amazon Portal Section */}
        <Section id="amazon" title="Amazon Hardening Project Portal" icon={Package} color="amber">
          <div className="space-y-4">
            <p className="text-slate-700 mb-4">
              A dedicated portal for the Amazon hardening project, with its own product range, datasheets and saved quotes.
              <strong> Visible to ZKTeco staff / admins only.</strong>
            </p>

            <Step number="1" title="Open the Amazon Portal">
              From the dashboard, click the <strong>Amazon (Hardening Project Portal)</strong> card. The portal uses its own
              login and dashboard, separate from the main configurator.
            </Step>

            <Step number="2" title="Build a Hardening Project Quote">
              Use the <strong>Hardening Project</strong> configurator to select Amazon-approved products and quantities.
            </Step>

            <Step number="3" title="Datasheets & Saved Quotes">
              Access Amazon-specific product datasheets and review or duplicate previously saved Amazon quotes from within the portal.
            </Step>

            <Tip>
              The Amazon portal is isolated from your standard catalogue, so its pricing and products won&apos;t mix with normal configurator quotes.
            </Tip>
          </div>
        </Section>

        {/* General Tips Section */}
        <Section id="tips" title="General Tips &amp; Tricks" icon={Lightbulb} color="amber">
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold text-slate-900 mb-3">Saving &amp; Managing Quotes</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Click &quot;Create Quote&quot; after generating to save to your account</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Use &quot;Download Quote&quot; to get an image file for sharing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Access saved quotes from the dashboard to edit or duplicate</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Export to CSV for use in spreadsheets or other systems</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold text-slate-900 mb-3">Pricing Information</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Distributors</strong> see cost price (blue) and sell price (green) columns</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Installers</strong> see their sell prices based on distributor pricing tiers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Use the discount field to apply project-specific adjustments</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold text-slate-900 mb-3">Getting Help</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Click the <HelpCircle className="h-4 w-4 inline text-blue-600" /> icons on configurator pages for field-specific help</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Use &quot;Request Project Support&quot; for complex configurations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Submit feature suggestions via the dashboard form</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Need more help? Contact your distributor or ZKTeco UK support team.</p>
        </div>
      </div>
    </div>
  );
};

export default HelpGuide;
